<template>
  <div class="erp-layout">
    <!-- ERP 상단 헤더 -->
    <header class="erp-header">
      <div class="erp-header-top">
        <div class="erp-brand">
          <div class="erp-logo">
            <i class="bi bi-hexagon-fill"></i>
          </div>
          <div class="erp-brand-text">
            <span class="erp-brand-name"><a href="/admin" class="erp-brand-name">마이툴</a></span>
            <span class="erp-brand-sub">Enterprise Resource Planning</span>
          </div>
        </div>

        <nav class="erp-main-nav">
          <ul class="erp-nav-list">
            <li class="erp-nav-item"
                v-for="(menu, index) in mainMenus"
                :key="index"
                :class="{ 'active': activeMenu === menu.id }">
              <a href="#" @click.prevent="setActiveMenu(menu)" class="erp-nav-link">
                <i :class="menu.icon"></i>
                <span>{{ menu.name }}</span>
              </a>
            </li>
          </ul>
        </nav>

        <div class="erp-header-actions">
          <!-- 알림 드롭다운 -->
          <div class="erp-action-item" title="알림" @click.stop="toggleNotification">
            <i class="bi bi-bell"></i>
            <span class="erp-badge" v-if="notifications.length > 0">{{ notifications.length }}</span>
            <div class="erp-dropdown erp-notification-dropdown" v-show="showNotification" @click.stop>
              <div class="erp-dropdown-header">
                <span>알림</span>
                <a href="#" @click.prevent="clearAllNotifications" v-if="notifications.length > 0">모두 읽음</a>
              </div>
              <div class="erp-dropdown-body">
                <div v-if="notifications.length === 0" class="erp-empty-state">
                  <i class="bi bi-bell-slash"></i>
                  <span>새로운 알림이 없습니다</span>
                </div>
                <div v-else class="erp-notification-list">
                  <div class="erp-notification-item" v-for="(noti, idx) in notifications" :key="idx">
                    <div class="erp-notification-icon" :class="noti.type">
                      <i :class="noti.icon"></i>
                    </div>
                    <div class="erp-notification-content">
                      <p class="erp-notification-title">{{ noti.title }}</p>
                      <span class="erp-notification-time">{{ noti.time }}</span>
                    </div>
                    <button class="erp-notification-close" @click.stop="removeNotification(idx)">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 설정 드롭다운 -->
          <div class="erp-action-item" title="설정" @click.stop="toggleSettings">
            <i class="bi bi-gear"></i>
            <div class="erp-dropdown erp-settings-dropdown" v-show="showSettings" @click.stop>
              <div class="erp-dropdown-header">
                <span>빠른 설정</span>
              </div>
              <div class="erp-dropdown-body">
                <router-link to="/admin/config/basic" class="erp-settings-item" @click="showSettings = false">
                  <i class="bi bi-sliders"></i>
                  <span>기본 설정</span>
                </router-link>
                <router-link to="/admin/config/users" class="erp-settings-item" @click="showSettings = false">
                  <i class="bi bi-people"></i>
                  <span>사용자 관리</span>
                </router-link>
                <router-link to="/admin/config/permissions" class="erp-settings-item" @click="showSettings = false">
                  <i class="bi bi-shield-lock"></i>
                  <span>권한 설정</span>
                </router-link>
                <router-link to="/admin/config/system" class="erp-settings-item" @click="showSettings = false">
                  <i class="bi bi-gear-wide-connected"></i>
                  <span>시스템 설정</span>
                </router-link>
              </div>
            </div>
          </div>

          <div class="erp-user-menu">
            <div class="erp-user-avatar">
              <i class="bi bi-person"></i>
            </div>
            <div class="erp-user-info">
              <span class="erp-user-name">관리자</span>
              <span class="erp-user-role">Administrator</span>
            </div>
            <div class="erp-user-dropdown">
              <a href="#" @click.prevent="logout" class="erp-dropdown-item">
                <i class="bi bi-box-arrow-right"></i>
                <span>로그아웃</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="erp-container">
      <!-- 좌측 사이드바 -->
      <aside class="erp-sidebar">
        <div class="erp-sidebar-header">
          <i :class="getActiveMenuIcon()" class="erp-sidebar-icon"></i>
          <span class="erp-sidebar-title">{{ getActiveMenuName() }}</span>
        </div>
        <nav class="erp-sidebar-nav">
          <ul class="erp-sidebar-menu">
            <li class="erp-sidebar-item"
                v-for="(subMenu, index) in getSubMenus()"
                :key="index">
              <router-link
                :to="subMenu.path"
                class="erp-sidebar-link"
                active-class="active">
                <i :class="subMenu.icon"></i>
                <span>{{ subMenu.name }}</span>
                <i class="bi bi-chevron-right erp-arrow"></i>
              </router-link>
            </li>
          </ul>
        </nav>
        <div class="erp-sidebar-footer">
          <div class="erp-system-status">
            <span class="erp-status-dot online"></span>
            <span class="erp-status-text">시스템 정상</span>
          </div>
        </div>
      </aside>

      <!-- 메인 콘텐츠 -->
      <main class="erp-main">
        <div class="erp-content-wrapper">
          <router-view />
        </div>
      </main>
    </div>

    <!-- ERP 하단 푸터 -->
    <footer class="erp-footer">
      <div class="erp-footer-content">
        <div class="erp-footer-left">
          <span class="erp-footer-copyright">&copy; 2025 Nebulasystem Corp.</span>
          <span class="erp-footer-divider">|</span>
          <span class="erp-footer-version">Version 2.1.0</span>
        </div>
        <div class="erp-footer-center">
          <a href="#" class="erp-footer-link">도움말</a>
          <a href="#" class="erp-footer-link">문의하기</a>
          <a href="#" class="erp-footer-link">이용약관</a>
          <a href="#" class="erp-footer-link">개인정보처리방침</a>
        </div>
        <div class="erp-footer-right">
          <span class="erp-footer-status">
            <i class="bi bi-shield-check"></i>
            보안 연결
          </span>
          <span class="erp-footer-time">
            <i class="bi bi-clock"></i>
            {{ currentTime }}
          </span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ShopLayout',
  data() {
    return {
      currentTime: '',
      activeMenu: 'config',  // 기본 선택 메뉴
      showNotification: false,
      showSettings: false,
      notifications: [
        { title: '새로운 주문이 접수되었습니다', time: '5분 전', icon: 'bi bi-cart-check', type: 'order' },
        { title: '재고 부족 알림: A제품', time: '30분 전', icon: 'bi bi-exclamation-triangle', type: 'warning' },
        { title: '시스템 업데이트 완료', time: '1시간 전', icon: 'bi bi-check-circle', type: 'success' }
      ],
      mainMenus: [
        { id: 'config', name: '환경설정', icon: 'bi bi-gear' },
        { id: 'vendor', name: '거래처관리', icon: 'bi bi-building' },
        { id: 'product', name: '제품관리', icon: 'bi bi-box' },
        { id: 'inventory', name: '재고관리', icon: 'bi bi-stack' },
        { id: 'quotes', name: '견적서관리', icon: 'bi bi-file-earmark-text' },
        { id: 'shoporders', name: '주문관리', icon: 'bi bi-cart' },
        // { id: 'order', name: '수주/견적관리', icon: 'bi bi-clipboard-check' },
        // { id: 'quotes', name: '견적관리', icon: 'bi bi-file-text' },
        // { id: 'shipping', name: '배송/출고관리', icon: 'bi bi-truck' },
        { id: 'promotion', name: '프로모션/쿠폰', icon: 'bi bi-tags' },
        { id: 'design', name: '디자인관리', icon: 'bi bi-palette' },
        { id: 'payment', name: '정산 및 외상결제관리', icon: 'bi bi-credit-card' },
        { id: 'stats', name: '매출/통계', icon: 'bi bi-graph-up' },
        // { id: 'review', name: '고객지원', icon: 'bi bi-currency-dollar' }
        { id: 'support', name: '고객지원', icon: 'bi bi-graph-up' }
      ],
      subMenus: {
        config: [
          { name: '기본 설정', path: '/admin/config/basic', icon: 'bi bi-sliders me-2' },
          { name: '사용자 관리', path: '/admin/config/users', icon: 'bi bi-people me-2' },
          { name: '권한 설정', path: '/admin/config/permissions', icon: 'bi bi-shield-lock me-2' },
          { name: '시스템 설정', path: '/admin/config/system', icon: 'bi bi-gear-wide-connected me-2' }
        ],
        vendor: [
          { name: '거래처 목록', path: '/admin/vendor/list', icon: 'bi bi-list-ul me-2' },
          { name: '거래처 등록', path: '/admin/vendor/register', icon: 'bi bi-plus-circle me-2' },
          { name: '거래처 등급', path: '/admin/vendor/level', icon: 'bi bi-plus-circle me-2' }
          // { name: '거래 내역', path: '/admin/vendor/transactions', icon: 'bi bi-journal-text me-2' }
        ],
        product: [
          { name: '제품 목록', path: '/admin/product/list', icon: 'bi bi-grid me-2' },
          // { name: '제품 등록', path: '/admin/product/register', icon: 'bi bi-plus-circle me-2' },
          { name: '카테고리 관리', path: '/admin/product/categories', icon: 'bi bi-diagram-3 me-2' },
          { name: '크레텍책임 제품연동', path: '/admin/product/cretec-sync', icon: 'bi bi-cloud-download me-2' }
        ],
        inventory: [
          { name: '재고 현황', path: '/admin/inventory/status', icon: 'bi bi-clipboard-data me-2' },
          // { name: '입고 관리', path: '/admin/inventory/incoming', icon: 'bi bi-box-arrow-in-down me-2' },
          // { name: '출고 관리', path: '/admin/inventory/outgoing', icon: 'bi bi-box-arrow-right me-2' },
          // { name: '재고 조정', path: '/admin/inventory/adjust', icon: 'bi bi-pencil-square me-2' }
        ],
        quotes: [
          { name: '견적서 목록', path: '/admin/quotes/list', icon: 'bi bi-list-ul me-2' }
        ],
        shoporders: [
          { name: '통합 주문 관리', path: '/admin/shoporders', icon: 'bi bi-list-ul me-2' }
          // { name: '신규 주문', path: '/admin/shoporders/new', icon: 'bi bi-plus-circle me-2' },
          // { name: '주문 처리중', path: '/admin/shoporders/processing', icon: 'bi bi-arrow-right-circle me-2' },
          // { name: '배송 준비중', path: '/admin/shoporders/preparing', icon: 'bi bi-box-seam me-2' },
          // { name: '배송중', path: '/admin/shoporders/shipping', icon: 'bi bi-truck me-2' },
          // { name: '배송완료', path: '/admin/shoporders/delivered', icon: 'bi bi-check-circle me-2' },
          // { name: '취소/반품/교환', path: '/admin/shoporders/cancelled', icon: 'bi bi-x-circle me-2' }
        ],
        // 다른 메뉴의 서브메뉴도 비슷한 방식으로 추가
        order: [
          { name: '수주 관리', path: '/admin/business/list', icon: 'bi bi-journal-plus me-2' },
          { name: '배송 현황', path: '/admin/business/shipping', icon: 'bi bi-truck me-2' },
          { name: '견적 목록', path: '/admin/business/estimates', icon: 'bi bi-file-earmark-text me-2' },
          { name: '견적 등록', path: '/admin/business/create', icon: 'bi bi-plus-circle me-2' }
        ],
        // quotes: [
        //   { name: '견적 목록', path: '/admin/quotes/estimates', icon: 'bi bi-file-earmark-text me-2' },
        //   { name: '견적 등록', path: '/admin/quotes/create', icon: 'bi bi-plus-circle me-2' }
        // ],

        shipping: [
          { name: '배송 목록', path: '/admin/shipping/list', icon: 'bi bi-truck me-2' },
          { name: '출고 목록', path: '/admin/shipping/dispatch', icon: 'bi bi-box-arrow-up-right me-2' },
          { name: '송장 등록', path: '/admin/shipping/invoice', icon: 'bi bi-receipt-cutoff me-2' },
          { name: '반품 관리', path: '/admin/shipping/returns', icon: 'bi bi-arrow-return-left me-2' },
          { name: '교환 관리', path: '/admin/shipping/exchanges', icon: 'bi bi-arrow-repeat me-2' }
        ],
        promotion: [
          // { name: '프로모션 관리', path: '/admin/promotion/list', icon: 'bi bi-megaphone me-2' },
          { name: '쿠폰 관리', path: '/admin/promotion/coupons', icon: 'bi bi-ticket-perforated me-2' }
        ],
        design: [
          { name: '메인디자인 관리', path: '/admin/design/main', icon: 'bi bi-house me-2' },
          // { name: '테마/레이아웃 관리', path: '/admin/design/theme', icon: 'bi bi-layout-text-window me-2' },
          { name: '배너 관리', path: '/admin/design/banner', icon: 'bi bi-image me-2' }
          // { name: '메뉴 관리', path: '/admin/design/menu', icon: 'bi bi-list me-2' },
          // { name: '로고/브랜딩', path: '/admin/design/branding', icon: 'bi bi-award me-2' },
          // { name: '모바일 디자인', path: '/admin/design/mobile', icon: 'bi bi-phone me-2' }
        ],
        payment: [
          { name: '정산 목록', path: '/admin/payment/settlements', icon: 'bi bi-cash-coin me-2' },
          { name: '외상 결제 현황', path: '/admin/payment/deferred', icon: 'bi bi-calendar-check me-2' },
          { name: '입금 내역', path: '/admin/payment/deposits', icon: 'bi bi-bank me-2' }
        ],
        stats: [
          { name: '매출 통계', path: '/admin/stats/sales', icon: 'bi bi-bar-chart-line me-2' },
          { name: '주문 통계', path: '/admin/stats/statsorders', icon: 'bi bi-clipboard-data me-2' },
          { name: '고객 분석', path: '/admin/stats/customers', icon: 'bi bi-people-fill me-2' }
        ],
        support: [
          // { name: '고객지원 대시보드', path: '/admin/support/dashboard', icon: 'bi bi-speedometer2 me-2' },
          { name: '공지사항 관리', path: '/admin/support/notice', icon: 'bi bi-megaphone me-2' },
          { name: 'FAQ 관리', path: '/admin/support/faq', icon: 'bi bi-question-circle me-2' },
          { name: '1:1 문의 관리', path: '/admin/support/inquiry', icon: 'bi bi-chat-dots me-2' },
          { name: '견적요청 관리', path: '/admin/support/quote-requests', icon: 'bi bi-file-earmark-text me-2' }
          // { name: '리뷰 관리', path: '/admin/support/reviews', icon: 'bi bi-star me-2' },
          // { name: '고객 피드백', path: '/admin/support/feedback', icon: 'bi bi-chat-square-heart me-2' }
        ]
        // 나머지 메뉴의 서브메뉴도 필요에 따라 추가
      }
    }
  },
  methods: {
    toggleNotification() {
      this.showNotification = !this.showNotification;
      this.showSettings = false;
    },
    toggleSettings() {
      this.showSettings = !this.showSettings;
      this.showNotification = false;
    },
    removeNotification(index) {
      this.notifications.splice(index, 1);
    },
    clearAllNotifications() {
      this.notifications = [];
    },
    closeAllDropdowns() {
      this.showNotification = false;
      this.showSettings = false;
    },
    logout() {
      if (confirm('정말 로그아웃 하시겠습니까?')) {
        // 관리자 토큰만 삭제 (같은 브라우저의 쇼핑몰 회원 로그인(auth_token, user_info)은 유지)
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('admin_user');

        console.log('🔐 관리자 로그아웃 완료');
        alert('로그아웃되었습니다.');

        // 🔥 페이지 새로고침으로 완전 초기화
        window.location.href = '/admin/login';
      }
    },
    setActiveMenu(menu) {
      this.activeMenu = menu.id;

      // 해당 메뉴의 첫 번째 서브메뉴로 라우팅 (선택 사항)
      const subMenus = this.getSubMenus();
      if (subMenus.length > 0) {
        this.$router.push(subMenus[0].path);
      }
    },
    getSubMenus() {
      return this.subMenus[this.activeMenu] || [];
    },
    getActiveMenuName() {
      const menu = this.mainMenus.find(m => m.id === this.activeMenu);
      return menu ? menu.name : '';
    },
    getActiveMenuIcon() {
      const menu = this.mainMenus.find(m => m.id === this.activeMenu);
      return menu ? menu.icon : 'bi bi-folder';
    },
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  created() {
    // URL 경로에 따라 초기 활성 메뉴 설정
    const path = this.$route.path;

    for (const menuId in this.subMenus) {
      const found = this.subMenus[menuId].some(subMenu => path.startsWith(subMenu.path));
      if (found) {
        this.activeMenu = menuId;
        break;
      }
    }

    // 시간 업데이트
    this.updateTime();
    setInterval(this.updateTime, 60000);
  },
  mounted() {
    // 외부 클릭 시 드롭다운 닫기
    document.addEventListener('click', this.closeAllDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeAllDropdowns);
  }
}
</script>

<style scoped>
/* ===== ERP 기본 레이아웃 ===== */
.erp-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f4f6f9;
  font-family: 'Segoe UI', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== 상단 헤더 ===== */
.erp-header {
  background: linear-gradient(180deg, #1e3a5f 0%, #152a45 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  position: sticky;
  top: 0;
}

.erp-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 56px;
}

/* 브랜드 로고 */
.erp-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.erp-logo {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #0066cc 0%, #004494 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.3);
}

.erp-logo i {
  color: #fff;
  font-size: 18px;
}

.erp-brand-text {
  display: flex;
  flex-direction: column;
}

.erp-brand-name {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.erp-brand-sub {
  color: #8fa8c8;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* 메인 네비게이션 */
.erp-main-nav {
  flex: 1;
  display: flex;
  justify-content: center;
  margin: 0 40px;
}

.erp-nav-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 2px;
}

.erp-nav-item {
  position: relative;
}

.erp-nav-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  color: #b8c9dc;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.erp-nav-link i {
  font-size: 14px;
}

.erp-nav-link:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.erp-nav-item.active .erp-nav-link {
  background: rgba(0, 102, 204, 0.3);
  color: #fff;
}

.erp-nav-item.active::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: #0066cc;
  border-radius: 2px 2px 0 0;
}

/* 헤더 액션 버튼 */
.erp-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.erp-action-item {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8c9dc;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.erp-action-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.erp-action-item i {
  font-size: 18px;
}

.erp-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* 사용자 메뉴 */
.erp-user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  margin-left: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  position: relative;
}

.erp-user-menu:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* 버튼과 드롭다운 사이 8px 빈틈에서 hover 가 끊겨 메뉴가 닫히지 않도록 보이지 않는 연결 영역 */
.erp-user-menu::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 10px;
}

.erp-user-avatar {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #0066cc 0%, #004494 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.erp-user-avatar i {
  color: #fff;
  font-size: 16px;
}

.erp-user-info {
  display: flex;
  flex-direction: column;
}

.erp-user-name {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.erp-user-role {
  color: #8fa8c8;
  font-size: 10px;
}

.erp-user-dropdown {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  overflow: hidden;
  z-index: 1001;
}

.erp-user-menu:hover .erp-user-dropdown {
  display: block;
}

.erp-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #374151;
  text-decoration: none;
  font-size: 13px;
  transition: all 0.2s ease;
}

.erp-dropdown-item:hover {
  background: #f3f4f6;
  color: #0066cc;
}

.erp-dropdown-item i {
  font-size: 16px;
}

/* ===== 공통 드롭다운 스타일 ===== */
.erp-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  min-width: 280px;
  overflow: hidden;
  z-index: 1002;
  border: 1px solid #e5e7eb;
}

.erp-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.erp-dropdown-header span {
  font-size: 13px;
  font-weight: 600;
  color: #1e3a5f;
}

.erp-dropdown-header a {
  font-size: 12px;
  color: #0066cc;
  text-decoration: none;
}

.erp-dropdown-header a:hover {
  text-decoration: underline;
}

.erp-dropdown-body {
  max-height: 320px;
  overflow-y: auto;
}

/* 알림 드롭다운 */
.erp-notification-dropdown {
  min-width: 320px;
}

.erp-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.erp-empty-state i {
  font-size: 32px;
  margin-bottom: 10px;
}

.erp-empty-state span {
  font-size: 13px;
}

.erp-notification-list {
  padding: 8px 0;
}

.erp-notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  transition: background 0.2s ease;
  position: relative;
}

.erp-notification-item:hover {
  background: #f8fafc;
}

.erp-notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.erp-notification-icon.order {
  background: #e0f2fe;
  color: #0284c7;
}

.erp-notification-icon.warning {
  background: #fef3c7;
  color: #d97706;
}

.erp-notification-icon.success {
  background: #d1fae5;
  color: #059669;
}

.erp-notification-icon i {
  font-size: 16px;
}

.erp-notification-content {
  flex: 1;
  min-width: 0;
}

.erp-notification-title {
  font-size: 13px;
  color: #374151;
  margin: 0 0 4px 0;
  line-height: 1.4;
}

.erp-notification-time {
  font-size: 11px;
  color: #9ca3af;
}

.erp-notification-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.erp-notification-item:hover .erp-notification-close {
  opacity: 1;
}

.erp-notification-close:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* 설정 드롭다운 */
.erp-settings-dropdown {
  min-width: 200px;
}

.erp-settings-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #374151;
  text-decoration: none;
  font-size: 13px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f3f4f6;
}

.erp-settings-item:last-child {
  border-bottom: none;
}

.erp-settings-item:hover {
  background: #f0f7ff;
  color: #0066cc;
}

.erp-settings-item i {
  font-size: 16px;
  width: 20px;
  text-align: center;
  color: #6b7280;
}

.erp-settings-item:hover i {
  color: #0066cc;
}

/* ===== 컨테이너 ===== */
.erp-container {
  display: flex;
  flex: 1;
}

/* ===== 좌측 사이드바 ===== */
.erp-sidebar {
  width: 240px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
}

.erp-sidebar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-bottom: 1px solid #e5e7eb;
}

.erp-sidebar-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #1e3a5f 0%, #152a45 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
}

.erp-sidebar-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e3a5f;
}

.erp-sidebar-nav {
  flex: 1;
  padding: 12px 0;
  overflow-y: auto;
}

.erp-sidebar-menu {
  list-style: none;
  margin: 0;
  padding: 0;
}

.erp-sidebar-item {
  margin: 2px 8px;
}

.erp-sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #4b5563;
  text-decoration: none;
  font-size: 13px;
  border-radius: 6px;
  transition: all 0.2s ease;
  position: relative;
}

.erp-sidebar-link i:first-child {
  font-size: 15px;
  width: 20px;
  text-align: center;
  color: #6b7280;
}

.erp-sidebar-link .erp-arrow {
  margin-left: auto;
  font-size: 10px;
  color: #9ca3af;
  opacity: 0;
  transition: all 0.2s ease;
}

.erp-sidebar-link:hover {
  background: #f3f4f6;
  color: #1e3a5f;
}

.erp-sidebar-link:hover i:first-child {
  color: #0066cc;
}

.erp-sidebar-link:hover .erp-arrow {
  opacity: 1;
  transform: translateX(2px);
}

.erp-sidebar-link.active {
  background: linear-gradient(90deg, #e8f0fe 0%, #f3f7ff 100%);
  color: #0066cc;
  font-weight: 500;
  border-left: 3px solid #0066cc;
  margin-left: 5px;
  padding-left: 9px;
}

.erp-sidebar-link.active i:first-child {
  color: #0066cc;
}

.erp-sidebar-link.active .erp-arrow {
  opacity: 1;
  color: #0066cc;
}

.erp-sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.erp-system-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.erp-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
}

.erp-status-dot.online {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
}

.erp-status-text {
  font-size: 11px;
  color: #6b7280;
}

/* ===== 메인 콘텐츠 ===== */
.erp-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.erp-content-wrapper {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* ===== 하단 푸터 ===== */
.erp-footer {
  background: #1e3a5f;
  border-top: 1px solid #152a45;
}

.erp-footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  font-size: 11px;
}

.erp-footer-left {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #8fa8c8;
}

.erp-footer-divider {
  color: #4a6282;
}

.erp-footer-version {
  background: rgba(0, 102, 204, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  color: #6ba3d6;
}

.erp-footer-center {
  display: flex;
  align-items: center;
  gap: 4px;
}

.erp-footer-link {
  color: #8fa8c8;
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.erp-footer-link:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.erp-footer-right {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #8fa8c8;
}

.erp-footer-status,
.erp-footer-time {
  display: flex;
  align-items: center;
  gap: 6px;
}

.erp-footer-status i {
  color: #10b981;
}

.erp-footer-time i {
  color: #6ba3d6;
}

/* ===== 반응형 디자인 ===== */
@media (max-width: 1200px) {
  .erp-nav-link span {
    display: none;
  }

  .erp-nav-link {
    padding: 8px 10px;
  }

  .erp-nav-link i {
    font-size: 16px;
  }
}

@media (max-width: 992px) {
  .erp-sidebar {
    width: 200px;
  }

  .erp-footer-center {
    display: none;
  }
}

@media (max-width: 768px) {
  .erp-header-top {
    padding: 0 12px;
  }

  .erp-brand-text {
    display: none;
  }

  .erp-main-nav {
    display: none;
  }

  .erp-sidebar {
    position: fixed;
    left: 0;
    top: 56px;
    bottom: 0;
    z-index: 999;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .erp-sidebar.open {
    transform: translateX(0);
  }

  .erp-user-info {
    display: none;
  }

  .erp-footer-content {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .erp-footer-right {
    flex-direction: column;
    gap: 4px;
  }
}

/* ===== 스크롤바 스타일 ===== */
.erp-sidebar-nav::-webkit-scrollbar,
.erp-content-wrapper::-webkit-scrollbar {
  width: 6px;
}

.erp-sidebar-nav::-webkit-scrollbar-track,
.erp-content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.erp-sidebar-nav::-webkit-scrollbar-thumb,
.erp-content-wrapper::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.erp-sidebar-nav::-webkit-scrollbar-thumb:hover,
.erp-content-wrapper::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
